import React, { FC, useState } from "react";
import {
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  SxProps,
} from "@mui/material";
import { Layout } from "./Layout";
import { LayoutDirection } from "./LayoutDirection";
import { RouteItem } from "./RouteItem";
import { isSelectableItem } from "./SelectableItem";
import { useClassName } from "./useResolveClassName";
import { ItemVariant } from "./ItemVariant";
import { useSxMerge } from "shared-ts-mui";
import { useResolveItemColors } from "./useResolveItemColors";
import { useResolveItemNameWithHiddenMeasurePct } from "./useResolveItemNameWithHiddenMeasure";

export interface RouteListItemProps {
  item: RouteItem;
  variant?: ItemVariant;
  layout: Layout;
  layoutDirection?: LayoutDirection;
  hideSelection?: boolean;
  hideLabel?: boolean;
  onSelected?: (item: RouteItem) => void;
  sx?: SxProps;
}

export const RouteListItem: FC<RouteListItemProps> = (props) => {
  const { item, onSelected } = props;
  const [element, setElement] = useState<HTMLLIElement | null>(null);
  const { name, measureRef } = useResolveItemNameWithHiddenMeasurePct(
    item,
    element,
    0.8,
  );
  const hideLabel = props.hideLabel ?? false;
  const selected = isSelectableItem(item) ? item.selected : undefined;
  const classNameRoot = useClassName(props, ["Item", "RouteListItem"]);
  const classNameButton = useClassName(props, ["Item", "RouteListItem-button"]);

  const handleClick = () => {
    onSelected?.(item);
  };

  const sx = useSxMerge(props.sx, {
    "&.variant-rounded": {
      borderRadius: "9px",
    },
    "&.direction-column": {
      "&.layout-medium": {
        borderRadius: "9px",
      },
    },
  });

  const {
    onColor,
    onContainerColorWhenSelected,
    containerColorWhenSelected,
    containerColorOnHover,
    containerColorWhenSelectedOnHover,
  } = useResolveItemColors();

  return (
    <ListItem
      ref={setElement}
      className={classNameRoot}
      disablePadding
      disableGutters
      sx={sx}
    >
      {/* Hidden measurement span for pre-measuring text overflow before paint */}
      <span
        ref={measureRef}
        style={{
          position: "absolute",
          visibility: "hidden",
          whiteSpace: "nowrap",
          pointerEvents: "none",
          font: "inherit",
          fontSize: "inherit",
          fontWeight: "inherit",
          letterSpacing: "inherit",
        }}
        aria-hidden="true"
      />
      <ListItemButton
        className={classNameButton}
        onClick={handleClick}
        sx={{
          borderRadius: "inherit",
          ">.MuiListItemText-root": {
            ">.MuiTypography-root": {
              fontSize: "inherit",
              textAlign: "inherit",
            },
          },
          "&.layout-medium": {
            fontSize: "0.75rem",
            "&.direction-column": {
              ".MuiListItemIcon-root": {
                height: "32px",
                width: "56px",
              },
            },
          },
          "&.layout-compact": {
            fontSize: "0.75rem",
            "&.direction-column": {
              ".MuiListItemIcon-root": {
                height: "32px",
                width: "56px",
              },
            },
          },
          "&.direction-row": {
            textAlign: "left",
            "&.selected": {
              color: onContainerColorWhenSelected,
              backgroundColor: containerColorWhenSelected,
            },
            "&:hover": {
              backgroundColor: selected
                ? containerColorWhenSelectedOnHover
                : containerColorOnHover,
            },
          },
          "&.direction-column": {
            flexDirection: "column",
            textAlign: "center",
            alignItems: "center",
            gap: "4px",
            ".MuiListItemIcon-root": {
              alignItems: "center",
              justifyContent: "center",
              borderRadius: "15px",
              color: onColor,
            },
            "&.selected": {
              backgroundColor: "unset",
              ".MuiListItemIcon-root": {
                backgroundColor: containerColorWhenSelected,
                color: onContainerColorWhenSelected,
              },
            },
            "&:hover": {
              backgroundColor: "unset",
              ".MuiListItemIcon-root": {
                backgroundColor: selected
                  ? containerColorWhenSelectedOnHover
                  : containerColorOnHover,
              },
            },
          },
        }}
      >
        {item.icon != null && <ListItemIcon>{item.icon}</ListItemIcon>}
        {!hideLabel && <ListItemText primary={name} />}
      </ListItemButton>
    </ListItem>
  );
};
